import React, { useEffect, useRef } from 'react';
// import { isEmpty } from 'lodash';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import { Box, Flex, Spinner, Button, Text } from '@chakra-ui/react';
import useSWRInfinite from 'swr/infinite';
import safeJsonStringify from 'safe-json-stringify';
import { useDebouncedCallback } from 'use-debounce';
import { Collections } from 'components/Collections';
import CardProfileCollection from 'components/CardProfileCollection';
import withSession from 'lib/session';
import { useHttp } from 'lib/useHttp';
import { API } from 'config/api';
import useOnScreen from 'hooks/useOnScreen';

const fetcher = (url) => fetch(url).then((res) => res.json());
const PAGE_SIZE = 12;

const getKey = (pageIndex, previousPageData, collectionId) => {
  if (previousPageData && !previousPageData.length) return null; // reached the end
  return `/api/collection/non_fungible_tokens?page=${pageIndex + 1}&id=${collectionId}`;
};

const CollectionPage = ({ slug, user, collection, access_token }) => {
  const moreRef = useRef();

  const { data, error, mutate, size, setSize, isValidating } = useSWRInfinite(
    (...args) => getKey(...args, collection.id),
    fetcher
  );

  const isVisible = useOnScreen(moreRef);

  const loadMore = useDebouncedCallback(() => {
    if (isVisible) {
      if (!isReachingEnd) {
        setSize(size + 1);
      }
    }
  }, 500);

  useEffect(() => {
    window.addEventListener('scroll', loadMore);
    return () => {
      window.removeEventListener('scroll', loadMore);
    };
  }, [loadMore]);

  const non_fungible_tokens = data ? [].concat(...data) : [];
  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data?.[0]?.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.length < PAGE_SIZE);

  const { data: favorites } = useHttp(`${process.env.NEXT_PUBLIC_API_URL}/me/favorites`, access_token);

  const minter = collection?.minter;

  const url = process.env.NEXT_PUBLIC_URL || 'https://staging.heylayer.com/';
  // const image = isEmpty(collection.non_fungible_tokens)
  //   ? 'https://i.imgur.com/aEsuOty.jpg'
  //   : collection.non_fungible_tokens[0].thumbnail_http_url;
  // console.log(image);

  const profileInfo = collection?.minter;
  // console.log('profileInfo', profileInfo);
  // console.log('collection', collection);

  const description =
    collection?.description ||
    'Try HeyLayer and start your NFT business or grow an existing one. Get more than NFT minting to manage every part of your business.';
  return (
    <Box id="content" minH="75vh">
      <NextSeo
        title={collection.name}
        description={description}
        canonical={`${url}/collection/${collection.id}`}
        openGraph={{
          type: 'website',
          url: url,
          title: collection.name,
          description: description,
          locale: 'en_EN',
          images: [
            {
              // url: image,
              width: 800,
              height: 600,
              alt: collection.name,
            },
          ],
          site_name: 'heylayer.com',
        }}
        twitter={{
          handle: '@HeyLayer',
          site: 'heylayer.com',
          cardType: 'summary_large_image',
        }}
      />
      <CardProfileCollection
        collection={collection}
        title={collection?.name}
        code={collection?.user?.country?.iso_alpha_2}
        user={`@${collection.user?.username}`}
        description={collection?.description}
        isVerified={collection?.user?.is_verified}
        userData={profileInfo}
        username={collection.user?.username}
        statistics={collection?.statistics}
      />
      <Collections favorites={favorites?.data || []} user={user} data={non_fungible_tokens} isCollection={true} />

      <div id="viewport" ref={moreRef}></div>
      <>
        {isLoadingMore ? (
          <Flex justifyContent="center" py="15px" mb="20px">
            <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
          </Flex>
        ) : isReachingEnd ? (
          <Flex justifyContent="center" py="15px" flexDirection="column" alignItems="center" minH="300px">
            {/* <Text mb="20px">No more data</Text> */}
            {user?.id === minter?.id ? (
              <Link href={`/create/single?collection=${slug}`}>
                <a>
                  <Button variant="black" w="240px" borderColor="#141414" mr="20px">
                    Add NFTs to Collection
                  </Button>
                </a>
              </Link>
            ) : (
              <Text mb="0px"></Text>
            )}
          </Flex>
        ) : (
          ''
        )}
      </>
    </Box>
  );
};

export const getServerSideProps = withSession(async function ({ req, res, query }) {
  const user = req.session.get('user');
  const access_token = user?.access_token || '';
  const { slug } = query;
  const collectionData = (await API.get(`/collection/${slug}`, { responseType: 'json' })).data;
  const collection = JSON.parse(safeJsonStringify(collectionData.data));

  return {
    props: { user: user || {}, access_token, slug, collection: collection || {} },
  };
});

export default CollectionPage;
